import { useEffect, useState } from 'react';
import { useFetch } from '../../../hooks/useFetch';
import type { FetchClient, RequestOptions } from '../../../lib/http/http.types';

/** A terrain row of GET /terrains (only the fields the perimeter tree needs). */
export interface Terrain {
  codeterrain: string;
  libelle: string | null;
  pays: string;
  actif: number;
}

interface PaginatedTerrains {
  data?: Terrain[];
  meta?: { totalPages?: number };
}

export interface UseTerrainsResult {
  terrains: Terrain[];
  loading: boolean;
  error: Error | null;
}

// GET /terrains caps `limit` at 100 — page through to load them all (HOMCO-1249).
const PAGE_SIZE = 100;

// Aggregate every page, failing loud on a shape change instead of silently truncating
// (same contract as instances' getTerrains) — a bad page must not drop terrains, which
// would wrongly shrink the perimeter tree.
async function loadAllTerrains(client: FetchClient, opts: RequestOptions): Promise<Terrain[]> {
  const fetchPage = (page: number) =>
    client.get<Terrain[] | PaginatedTerrains>(`/terrains?limit=${PAGE_SIZE}&page=${page}`, opts);

  const first = await fetchPage(1);
  if (Array.isArray(first)) return first; // non-paginated response (forward-compat)
  if (!Array.isArray(first?.data)) {
    throw new Error('useTerrains: page 1 returned a non-paginated response');
  }

  const all: Terrain[] = [...first.data];
  const totalPages = first.meta?.totalPages ?? 1;
  for (let page = 2; page <= totalPages; page++) {
    const res = await fetchPage(page);
    if (Array.isArray(res) || !Array.isArray(res?.data)) {
      throw new Error(`useTerrains: page ${page}/${totalPages} returned a non-paginated response`);
    }
    all.push(...res.data);
  }
  return all;
}

/** Loads every terrain (all pages) for the perimeter selector (HOMCO-1249). */
export function useTerrains(): UseTerrainsResult {
  const [client] = useFetch();
  const [terrains, setTerrains] = useState<Terrain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    let active = true;

    const run = async () => {
      // useFetch recreates its client on token/auth change, re-running this effect. Reset
      // loading/error on every (re)load, otherwise a successful reload after an earlier
      // failure would keep the stale error (edit button stuck disabled) and leave `loading`
      // false while the new request is in flight.
      setLoading(true);
      setError(null);
      try {
        const all = await loadAllTerrains(client, { signal: ctrl.signal });
        if (active) setTerrains(all);
      } catch (err) {
        if (!active || (err instanceof Error && err.name === 'AbortError')) return;
        setTerrains([]);
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        if (active) setLoading(false);
      }
    };

    void run();
    return () => {
      active = false;
      ctrl.abort();
    };
  }, [client]);

  return { terrains, loading, error };
}
